/**
 * particleShaders.ts
 * شيدرات محرّك جسيمات الرياح — إعادة بناء كاملة بأسلوب Zoom Earth.
 *
 * الفكرة الجوهرية (وهي ما يفرّق هذا المحرّك عن webgl-wind الأصلي): **المحاكاة تجري في
 * فضاء الشاشة**، لا في فضاء الشبكة الجغرافية. كل جسيمة تُخزَّن كنقطة داخل نافذة العرض
 * (نسبة [0,1] من العرض/الارتفاع)، وتتحرّك عدداً ثابتاً من **البكسلات** لكل (م/ث) من
 * سرعة الرياح — تماماً كما يفعل Zoom Earth (px += u · 0.075 / max(1, 5 − zoom)).
 *
 * نتائج هذا الاختيار:
 *  - السرعة البصرية ثابتة في كل مستويات الزووم بلا أي معايرة أُسّية.
 *  - دقّة التخزين = عرض النافذة ÷ 65535 ≈ 0.02 بكسل في كل زووم (لا تجمّد عند الزووم العالي).
 *  - تغيّر العرض (سحب/تكبير/تدوير) = تحويل إسقاطي واحد (homography) يُطبَّق على مواضع
 *    الجسيمات **وعلى نسيج الذيول معاً**، فيبقى كل شيء ملتصقاً بالأرض أثناء الحركة بلا
 *    مسح ولا إعادة بذر ولا قفزات.
 *
 * الحالة تُخزَّن في نسيجَي RGBA8 (لا حاجة لأي إضافة float — سلوك واحد على كل جهاز):
 *  - نسيج الموضع: x بـ16-bit في (r,g) و y بـ16-bit في (b,a).
 *  - نسيج العمر:  العمر المطبّع بـ16-bit في (r,g)، بذرة عشوائية ثابتة للجسيمة في (b)،
 *                 وعلَم "وُلدت هذا الإطار" في (a).
 */

// ── ترميز/فكّ الحالة ──────────────────────────────────────────────────────────
export const STATE_CODEC = `
vec2 decodeFix16(const vec4 c) {
    return vec2(c.r * (255.0 * 256.0) + c.g * 255.0,
                c.b * (255.0 * 256.0) + c.a * 255.0) / 65535.0;
}
vec4 encodeFix16(const vec2 p) {
    vec2 q  = floor(clamp(p, 0.0, 1.0) * 65535.0 + 0.5);
    vec2 hi = floor(q * (1.0 / 256.0));
    vec2 lo = q - hi * 256.0;
    return vec4(hi.x, lo.x, hi.y, lo.y) / 255.0;
}
float decodeAge(const vec4 c) {
    return (c.r * (255.0 * 256.0) + c.g * 255.0) / 65535.0;
}
vec4 encodeAge(const float age, const float seed, const float born) {
    float q  = floor(clamp(age, 0.0, 1.0) * 65535.0 + 0.5);
    float hi = floor(q * (1.0 / 256.0));
    float lo = q - hi * 256.0;
    return vec4(hi / 255.0, lo / 255.0, seed, born);
}
`;

const RAND = `
float rand(const vec2 co) {
    float t = dot(vec2(12.9898, 78.233), co);
    return fract(sin(t) * (4375.85453 + t));
}
`;

// ── تمريرة المحاكاة ───────────────────────────────────────────────────────────

/** رأس بسيط يغطي نسيج الحالة res×res: كل شظية تقرأ وتكتب نفس التكسل. */
export const STATE_VERT = `
attribute vec2 a_pos;
varying vec2 v_uv;
void main() {
    v_uv = a_pos;
    gl_Position = vec4(a_pos * 2.0 - 1.0, 0.0, 1.0);
}`;

/**
 * تحديث الجسيمات. يُنفَّذ مرّتين بنفس البرنامج (u_out_age) — مرّة إلى نسيج الموضع ومرّة
 * إلى نسيج العمر. التمريرتان تقرآن نفس الحالة القديمة وتحسبان نفس قرار الموت، فتبقيان
 * متّسقتين تماماً بلا حاجة إلى MRT أو إضافات.
 */
export const UPDATE_FRAG = `
precision highp float;

uniform sampler2D u_pos;
uniform sampler2D u_age;
uniform sampler2D u_wind;      // حقل الساعة الأدنى
uniform sampler2D u_wind_next; // حقل الساعة الأعلى
uniform float u_wind_blend;    // 0..1 بين الحقلين — متّصل تماماً (لا خطوات)
uniform mat3  u_prev_to_cur;   // شاشة الإطار السابق → شاشة الإطار الحالي
uniform mat3  u_s2m;           // شاشة الإطار الحالي → مركاتور [0,1] (قد يتجاوزها مع نسخ العالم)
uniform vec4  u_bounds;        // حدود شبكة الرياح: west, south, east, north
uniform float u_wrap_x;        // 1 إذا كانت الشبكة تغطي الكرة (التفاف الطول)
uniform vec2  u_step;          // (K/العرض, K/الارتفاع) بوحدات CSS — K = بكسل لكل (م/ث) لكل إطار
uniform float u_frame_step;    // زمن الإطار بوحدات 60fps
uniform float u_age_step;      // frame_step ÷ العمر الأقصى (بالإطارات)
uniform float u_margin;        // هامش التخزين خارج النافذة
uniform float u_rand_seed;
uniform float u_reseed;        // 1 → إعادة بذر كل الجسيمات هذا الإطار
uniform float u_out_age;       // 0 → أخرج الموضع، 1 → أخرج العمر
uniform float u_uv_max;        // م/ث عند أقصى قيمة مرمَّزة في النسيج
uniform float u_scale_kill;    // احتمال موت إضافي يحفظ الكثافة عند التصغير (انظر أدناه)

varying vec2 v_uv;

const float PI = 3.141592653589793;
` + STATE_CODEC + RAND + `

vec2 storedToScreen(const vec2 q) { return q * (1.0 + 2.0 * u_margin) - u_margin; }
vec2 screenToStored(const vec2 s) { return (s + u_margin) / (1.0 + 2.0 * u_margin); }

/**
 * عيّنة من حقل الرياح (U في G وV في B) باستيفاء ثنائي الخطّية من الـ GPU.
 *
 * جُرّب هنا استيفاء مستمرّ المشتقّة (ترجيح أوزان الاستيفاء بـ smoothstep، أربع عيّنات
 * يدوية) على أمل إزالة انكسار المسار عند حدود الخلايا، وقيس على مسار جسيمة داخل مركز
 * إعصار بشبكة 720×361 حقيقية: أقصى قفزة انحناء 1.37° مقابل 1.32° لثنائي الخطّية، ومتوسط
 * 0.38° مقابل 0.30° — أي **أسوأ قليلاً**، لأن smoothstep يزيل انكسار المشتقّة عند الحدّ
 * لكنه يشوّه الحقل داخل الخلية فيضيف انحناءً من عنده. فبقي الاستيفاء العادي (وهو أرخص).
 * مصدر الانكسارات المرئي كان القفز الزمني لا المكاني — راجع u_wind_blend.
 */
vec2 sampleWind(sampler2D tex, const vec2 tc) {
    return texture2D(tex, tc).gb;
}

void main() {
    vec4 pc = texture2D(u_pos, v_uv);
    vec4 ac = texture2D(u_age, v_uv);

    vec2  sPrev = storedToScreen(decodeFix16(pc));
    float age   = decodeAge(ac);
    float seed  = ac.b;

    // 1) نقل الجسيمة من فضاء شاشة الإطار السابق إلى فضاء شاشة هذا الإطار.
    //    هذا هو ما يجعل الجسيمات ملتصقة بالأرض أثناء السحب والتكبير بلا إعادة بذر.
    vec3 hp = u_prev_to_cur * vec3(sPrev, 1.0);
    vec2 s  = hp.xy / hp.z;

    // 2) الموقع الجغرافي لأخذ عيّنة الرياح.
    vec3  hm   = u_s2m * vec3(s, 1.0);
    vec2  merc = hm.xy / hm.z;
    float lat  = degrees(2.0 * atan(exp(PI * (1.0 - 2.0 * merc.y))) - PI * 0.5);
    float lng  = merc.x * 360.0 - 180.0;

    vec2 tc;
    tc.x = (lng - u_bounds.x) / (u_bounds.z - u_bounds.x);
    tc.x = mix(clamp(tc.x, 0.0, 1.0), fract(tc.x), u_wrap_x);
    tc.y = (lat - u_bounds.y) / (u_bounds.w - u_bounds.y);

    // U في القناة G وV في القناة B (ترميز buildValueTexture) → م/ث.
    // المزج الزمني هنا على الـ GPU لكل إطار (لا خطوات 10 دقائق على الـ CPU)، والترميز
    // خطّي فمزج القيم المرمَّزة يكافئ مزج القيم المفكوكة.
    vec2 wind = (mix(sampleWind(u_wind, tc), sampleWind(u_wind_next, tc), u_wind_blend)
                 * 2.0 - 1.0) * u_uv_max;

    // إزاحة بالبكسل: عدد ثابت من البكسلات لكل (م/ث) — لا تشوّه مركاتور ولا اعتماد على
    // الزووم (السرعة البصرية ثابتة كما Zoom Earth). y الشاشة نحو الأسفل فنقلب مركّبة V.
    vec2 ds = vec2(wind.x, -wind.y) * u_step * u_frame_step;

    // اهتزاز بمقدار كمّة تخزين واحدة: يمنع تجمّد الرياح الضعيفة جداً على شبكة 16-bit
    // (خطوة أصغر من الكمّة تُقرَّب لنفس القيمة كل إطار) مع بقاء متوسط الحركة صحيحاً.
    vec2 dseed  = (s + v_uv) * (u_rand_seed + 1.7);
    vec2 dither = (vec2(rand(dseed + 3.1), rand(dseed + 4.3)) - 0.5)
                * ((1.0 + 2.0 * u_margin) / 65535.0);

    vec2 sNew = s + ds + dither;

    // عمر كل جسيمة يُقاس نسبةً إلى عمر أقصى **خاصّ بها** (0.6×..1.4× من العمر المضبوط،
    // من بذرتها الثابتة). لولا ذلك لماتت كل الجسيمات التي وُلدت معاً — بعد تكبير مثلاً —
    // في اللحظة نفسها بعد دورة حياة واحدة، فيظهر خفوت جماعي دوري.
    float lifeScale = 0.6 + 0.8 * seed;
    float ageNew    = age + u_age_step / lifeScale;

    // الموت: بلوغ العمر الأقصى، أو الخروج من النافذة (مع الهامش)، أو تجاوز حدّ مركاتور،
    // أو الموت "المحافظ على الكثافة" عند التصغير: حين تتقلّص الخريطة بمعامل خطّي λ في
    // إطار واحد تنضغط الجسيمات في 1/λ² من مساحة الشاشة وتبقى الحلقة الجديدة فارغة؛
    // إماتة نسبة (1 − λ²) منها وإعادة بذرها موحّدةً على النافذة الجديدة تعيد الكثافة
    // موحّدة فوراً بدل انتظار دورة حياة كاملة.
    vec2  kseed = (v_uv + vec2(0.11, 0.83)) * (u_rand_seed + 3.1);
    float scaleKill = step(1.0 - u_scale_kill, rand(kseed));

    float outside =
        step(sNew.x, -u_margin) + step(1.0 + u_margin, sNew.x) +
        step(sNew.y, -u_margin) + step(1.0 + u_margin, sNew.y) +
        step(merc.y, 0.0)       + step(1.0, merc.y);
    float dead = step(0.5,
        step(1.0, ageNew) + step(0.5, outside) + step(0.5, u_reseed) + scaleKill);

    // الولادة: موضع عشوائي موحّد داخل النافذة (مع الهامش) وبذرة جديدة.
    // العمر الابتدائي = 0 عادةً، وعشوائي فقط عند إعادة البذر الجماعية كي لا تموت كل
    // الجسيمات معاً بعد دورة حياة واحدة (نفس ما يفعله Zoom Earth عند تبديل الوقت).
    vec2  rseed     = (v_uv + vec2(0.37, 0.71)) * (u_rand_seed + 2.3);
    vec2  sSpawn    = vec2(rand(rseed + 1.3), rand(rseed + 2.1)) * (1.0 + 2.0 * u_margin) - u_margin;
    float ageSpawn  = u_reseed * rand(rseed + 7.7);
    float seedSpawn = rand(rseed + 9.5);

    vec2  sOut    = mix(sNew,   sSpawn,    dead);
    float ageOut  = mix(ageNew, ageSpawn,  dead);
    float seedOut = mix(seed,   seedSpawn, dead);

    if (u_out_age > 0.5) {
        gl_FragColor = encodeAge(ageOut, seedOut, dead);
    } else {
        gl_FragColor = encodeFix16(screenToStored(sOut));
    }
}`;

// ── تمريرة نسيج الذيول ────────────────────────────────────────────────────────

/**
 * رباعي مشوَّه بمصفوفة إسقاطية: يُستخدم لإعادة إسقاط نسيج الذيول من فضاء شاشة الإطار
 * السابق إلى فضاء هذا الإطار، فتبقى الذيول ملتصقة بالأرض أثناء السحب والتكبير بدل
 * مسحها. (u_warp = الوحدة عند الدمج النهائي على الشاشة.)
 *
 * نضرب clip بـ w = المركّبة المتجانسة كي يستوفي الـ GPU إحداثيات النسيج استيفاءً
 * منظوريّاً صحيحاً حين تكون الخريطة مائلة (pitch) — عند pitch=0 تكون w=1 بالضبط.
 */
export const SCREEN_VERT = `
precision highp float;
attribute vec2 a_pos;          // ركن المربّع الواحد: (0,0) أعلى-يسار المصدر
uniform mat3 u_warp;
varying vec2 v_tc;
void main() {
    v_tc = vec2(a_pos.x, 1.0 - a_pos.y);
    vec3 p = u_warp * vec3(a_pos, 1.0);
    float w = max(p.z, 1e-6);
    vec2  s = p.xy / w;
    gl_Position = vec4((s.x * 2.0 - 1.0) * w, (1.0 - s.y * 2.0) * w, 0.0, w);
}`;

/**
 * نسخ نسيج الذيول مع تلاشٍ. الطرح الصغير (u_floor) ضروري: الضرب وحده على RGBA8 يعلَق
 * عند القيم الصغيرة (0.95 × 1/255 يُقرَّب إلى 1/255 نفسه) فتبقى بقايا ذيول باهتة إلى
 * الأبد على الشاشة؛ الطرح يضمن الوصول إلى الصفر.
 */
export const SCREEN_FRAG = `
precision mediump float;
uniform sampler2D u_screen;
uniform float u_opacity;
uniform float u_floor;
varying vec2 v_tc;
void main() {
    if (v_tc.x < 0.0 || v_tc.x > 1.0 || v_tc.y < 0.0 || v_tc.y > 1.0) {
        gl_FragColor = vec4(0.0);
        return;
    }
    gl_FragColor = max(texture2D(u_screen, v_tc) * u_opacity - u_floor, 0.0);
}`;

// ── تمريرة رسم الجسيمات ───────────────────────────────────────────────────────

/**
 * كل جسيمة = كبسولة من موضعها في الإطار السابق إلى موضعها الآن (٦ رؤوس/مثلثان).
 * الذيل المرئي يُبنى من تراكم هذه القطع في نسيج الذيول مع التلاشي — لا من شريط هندسي
 * طويل. الموضع السابق يُقرأ من نسيج الحالة نفسه (لا يُقدَّر بخطوة رياح عكسية)، فيطابق
 * المسار تماماً حتى في الدوران الحادّ قرب مركز الإعصار.
 *
 * a_corner: 0/1 = طرف الرأس (يسار/يمين)، 2/3 = طرف الذيل.
 */
export const DRAW_VERT = `
precision highp float;

attribute float a_index;
attribute float a_corner;

uniform sampler2D u_pos_new;
uniform sampler2D u_pos_old;
uniform sampler2D u_age_new;
uniform mat3  u_prev_to_cur;
uniform float u_res;
uniform vec2  u_resolution;    // أبعاد الإطار بالبكسل الفيزيائي
uniform float u_margin;
uniform float u_width;         // سمك الخط بالبكسل الفيزيائي
uniform float u_width_var;
uniform float u_alpha;
uniform float u_max_seg;       // أقصى طول قطعة بالبكسل (حماية من القفزات)
uniform float u_min_seg;       // أدنى طول شرطة بالبكسل (وضوح في الرياح الضعيفة)

varying float v_across;
varying float v_along;
varying float v_cap_t;
varying float v_alpha;
varying float v_half;

` + STATE_CODEC + `

vec2 storedToScreen(const vec2 q) { return q * (1.0 + 2.0 * u_margin) - u_margin; }

void main() {
    vec2 cell = (vec2(mod(a_index, u_res), floor(a_index / u_res)) + 0.5) / u_res;

    vec4  ac     = texture2D(u_age_new, cell);
    float age    = decodeAge(ac);
    float jitter = ac.b;
    float born   = ac.a;

    vec2 sHead = storedToScreen(decodeFix16(texture2D(u_pos_new, cell)));
    vec2 sPrev = storedToScreen(decodeFix16(texture2D(u_pos_old, cell)));

    // الموضع السابق مخزَّن بفضاء شاشة الإطار السابق → أعِد إسقاطه إلى فضاء هذا الإطار.
    vec3 hp    = u_prev_to_cur * vec3(sPrev, 1.0);
    vec2 sTail = hp.xy / hp.z;
    // الجسيمة المولودة للتوّ لا ذيل لها (وإلا ظهر خطّ يعبر الشاشة من موضعها السابق).
    sTail = mix(sTail, sHead, step(0.5, born));

    vec2  head = sHead * u_resolution;
    vec2  tail = sTail * u_resolution;
    float len  = length(head - tail);
    tail = mix(tail, head, step(u_max_seg, len));   // قفزة شاذة → نقطة

    vec2  d   = head - tail;
    len       = length(d);
    vec2  dir = len > 0.0001 ? d / len : vec2(1.0, 0.0);

    // حدّ أدنى لطول الشرطة: في الرياح الضعيفة تكون إزاحة الإطار أقلّ من بكسل، فتُرسم
    // الجسيمة نقطةً باهتة بدل خيط. نمدّ الشرطة إلى الخلف على اتجاه الحركة نفسه (فوق
    // مسارها المرسوم أصلاً في نسيج الذيول) فتقرأ كخيط واضح كما في Windy/Zoom Earth بدل
    // نقاط متناثرة، ومن دون أن يتحرّك رأس الجسيمة عن موضعه الحقيقي.
    len  = max(len, u_min_seg);
    tail = head - dir * len;

    float halfWidth = max(0.35, u_width * 0.5 * (1.0 + (jitter - 0.5) * u_width_var));
    vec2  nrm       = vec2(-dir.y, dir.x) * halfWidth;

    // الظهور والاختفاء التدريجيان: لا تولد الجسيمة ولا تموت فجأة، فلا وميض.
    // نافذة الظهور (0.11 من العمر ≈ 31 إطاراً) مضبوطة عمداً لتغطّي معظم مدّة **بناء
    // الذيل**: الجسيمة الوليدة خيطها أقصر من طوله الصحيح حتى تقطع ~49 إطاراً، فإبقاؤها
    // باهتة خلال ذلك يمنع قراءة خيوط قصيرة على أنها "رياح ضعيفة" في مكان رياحه قوية.
    v_alpha = u_alpha * smoothstep(0.0, 0.11, age) * (1.0 - smoothstep(0.88, 1.0, age));

    bool  headVertex = a_corner < 1.5;
    float side       = mod(a_corner, 2.0) < 0.5 ? 1.0 : -1.0;

    v_across = side;
    v_along  = headVertex ? 1.0 : 0.0;
    v_half   = halfWidth;
    v_cap_t  = halfWidth / max(len + 2.0 * halfWidth, 0.0001);

    vec2 center = headVertex ? head + dir * halfWidth : tail - dir * halfWidth;
    vec2 s      = (center + nrm * side) / u_resolution;

    gl_Position = vec4(s.x * 2.0 - 1.0, 1.0 - s.y * 2.0, 0.0, 1.0);
}`;

/** كبسولة بحواف دائرية وحافة تمويه بعرض بكسل واحد — خيط أبيض رفيع نظيف كما Zoom Earth. */
export const DRAW_FRAG = `
precision mediump float;

varying float v_across;
varying float v_along;
varying float v_cap_t;
varying float v_alpha;
varying float v_half;

uniform vec3 u_color;

void main() {
    float capT = max(v_cap_t, 0.0001);
    float dist = abs(v_across);
    if (v_along < capT) {
        dist = length(vec2(v_across, v_along / capT - 1.0));
    } else if (v_along > 1.0 - capT) {
        dist = length(vec2(v_across, (v_along - (1.0 - capT)) / capT));
    }

    // مسافة الحافة بالبكسل → تمويه ثابت بعرض بكسل مهما رقّ الخط. الأرضية 1.0 تضمن أن
    // يبلغ قلب الخط ألفا كاملة حتى على شاشة بكثافة 1× (وإلا بدت الخطوط الرفيعة باهتة).
    float alpha = v_alpha * clamp((1.0 - dist) * max(v_half, 1.0), 0.0, 1.0);
    if (alpha < 0.003) discard;

    // لون مسبق الضرب بالألفا (يطابق ONE / ONE_MINUS_SRC_ALPHA).
    gl_FragColor = vec4(u_color * alpha, alpha);
}`;
